import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchEstates, fetchRegions } from 'shared/stores/store';
import SearchForm from "./SearchForm";
import TestimonialSlider from "./Widgets/TestimonialSlider";
import { useTranslation } from "react-i18next";
import NewestEstates from "./Estates/EstateGrid";
import { formatPriceSummary } from 'shared/utils/currency';

export default function HomePage() {
    const dispatch = useDispatch();
    const { t } = useTranslation('common');
    const estates = useSelector((state) => state.estates.list);
    const loading = useSelector((state) => state.estates.loading);
    const error = useSelector((state) => state.estates.error);
    const [currentSlide, setCurrentSlide] = useState(0);
    const regions = useSelector((state) => state.regions.list);

    // Touch/swipe state for slideshow
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // Ref to store advanced search handler
    const advancedSearchHandlerRef = React.useRef(null);

    useEffect(() => {
        if (!estates || estates.length === 0) return;

        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % Math.min(estates.length, 5));
        }, 5000); // Change slide every 5 seconds

        return () => clearInterval(timer);
    }, [estates]);

    // Touch handlers for slideshow swipe
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && estates && estates.length > 0) {
            setCurrentSlide((prev) => (prev + 1) % Math.min(estates.length, 5));
        }
        if (isRightSwipe && estates && estates.length > 0) {
            setCurrentSlide((prev) => (prev - 1 + Math.min(estates.length, 5)) % Math.min(estates.length, 5));
        }

        // Reset values
        setTouchStart(null);
        setTouchEnd(null);
    };

    useEffect(() => {
        dispatch(fetchRegions());
        dispatch(fetchEstates());
    }, [dispatch]);

    const AdvancedSearchButton = () => {
        const handleClick = (e) => {
            e.preventDefault();
            if (advancedSearchHandlerRef.current) {
                advancedSearchHandlerRef.current();
            } else {
                // Fallback if handler not ready
                window.location.href = '/listings?advanced';
            }
        };

        return (
            <button
                onClick={handleClick}
                className="tracking-wide text-xs uppercase rounded-full bg-transparent border-white text-white border-0 font-semibold py-2 px-4 whitespace-nowrap hover:underline"
            >
                {t('home.hero.advancedSearch')}
                <span aria-hidden="true">→</span>
            </button>
        );
    };

    return (
        <>
            <div className="bg-white h-auto md:h-[calc(100vh-69px)] hero-tall-screen">
                <div
                    className="relative bg-gray-600 h-auto md:h-[calc(100vh-69px)] hero-tall-screen"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {estates && estates.map((estate, index) => (
                        <div
                            key={`estate-slide-${estate.id}`}
                            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                                }`}
                        >
                            <div aria-hidden="true" className="absolute inset-0 overflow-hidden h-full md:h-[calc(100vh-69px)] hero-tall-screen">
                                <img
                                    src={estate.thumb}
                                    alt={estate.title}
                                    className="h-full w-full object-cover object-center"
                                />
                            </div>
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 bg-gradient-to-b bg-gray-900 opacity-60 h-full md:h-[calc(100vh-69px)] hero-tall-screen"
                            />
                        </div>
                    ))}

                    {estates && estates.length > 0 && (
                        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
                            {estates.slice(0, 5).map((_, index) => (
                                <button
                                    key={`estate-button-${index}`}
                                    className={`w-2 h-2 rounded-full transition-colors ${index === currentSlide ? 'bg-white' : 'bg-white/50'
                                        }`}
                                    onClick={() => setCurrentSlide(index)}
                                />
                            ))}
                        </div>
                    )}

                    {/* Estate info */}
                    {estates && estates.length > 0 && (
                        <div className="hidden md:block absolute right-20 md:right-10 bottom-16 md:bottom-20">
                            <img
                                alt="Company name"
                                src="/white-logo-square-inb.png"
                                className="hidden md:block h-24 mx-auto mb-6"
                            />
                            <Link
                                to={`/estate/${estates[currentSlide].id}`}
                                className="text-xs rounded-full bg-white font-semibold md:text-sm py-1.5 md:py-2 px-4 text-sky-900 inline-block"
                            >
                                {t(`search.propertyTypes.options.${estates[currentSlide].type.toLowerCase()}`)} {t('home.hero.propertyLink.in')} {estates[currentSlide].region_name} {' '}
                                {t('home.hero.propertyLink.from')} {' '}
                                <span className="ml-0 mr-2 inline-block">
                                    {formatPriceSummary(estates[currentSlide].price)}
                                </span>
                                <span aria-hidden="true">→</span>
                            </Link>
                        </div>
                    )}

                    <div className="relative flex max-w-md flex-col px-6 pt-20 pb-16 sm:pt-16 sm:pb-24 md:pl-10">
                        <h1 className="text-4xl font-bold tracking-tight text-white lg:text-5xl">
                            {t('home.hero.title')}
                        </h1>
                        <p className="mt-2 md:mt-6 text-sm md:text-base font-semibold text-white">
                            {t('home.hero.subtitle')}
                        </p>

                        <div className="relative mt-4">
                            <SearchForm onAdvancedSearch={(handler) => {
                                advancedSearchHandlerRef.current = handler;
                            }} />
                            <div className="flex justify-end md:justify-end mt-4">
                                <AdvancedSearchButton />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Hero with Search */}
            <div className="bg-white pt-10">
                <main className="mx-auto max-w-2xl px-4 lg:max-w-7xl lg:px-8 bg-white">
                    <div className="pb-24 pt-12">
                        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-0">
                            <h2 id="trending-heading" className="text-2xl font-bold tracking-tight text-gray-900">
                                {t('home.sections.latestProperties.title')}
                            </h2>
                            <Link to="/listings"
                                className="hidden text-sm font-semibold text-sky-600 hover:text-sky-500 sm:block">
                                {t('home.sections.latestProperties.viewAll')}
                                <span aria-hidden="true"> →</span>
                            </Link>
                        </div>

                        <div>
                            <NewestEstates></NewestEstates>
                        </div>
                    </div>
                </main>
                <div className="relative">
                    <img
                        src="https://images.pexels.com/photos/1791684/pexels-photo-1791684.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                        alt="Beautiful landscape in Spain"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gray-900/70"></div>
                    <div className="relative px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
                        <div className="mx-auto max-w-2xl text-center">
                            <h2 className="text-balance text-4xl leading-10 font-semibold tracking-tight text-white sm:text-5xl">
                                {t('home.sections.createProfile.title')}
                            </h2>
                            <p className="mx-auto mt-6 max-w-xl text-pretty text-lg/8 text-white">
                                {t('home.sections.createProfile.description')}
                            </p>
                            <div className="mt-10 flex items-center justify-center gap-x-6">
                                <Link
                                    to="/register"
                                    className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-sky-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                                >
                                    {t('home.sections.createProfile.buttons.create')}
                                </Link>
                                <Link to="/login" className="text-sm font-semibold leading-6 text-white">
                                    {t('home.sections.createProfile.buttons.login')} <span aria-hidden="true">→</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
                <section aria-labelledby="trending-heading" className="bg-white">
                    <div className="py-16 sm:py-24 lg:mx-auto lg:max-w-7xl lg:px-8 lg:py-32">
                        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-0">
                            <h2 id="trending-heading" className="text-2xl font-bold tracking-tight text-gray-900">
                                {t('home.sections.popularAreas.title')}
                            </h2>
                            <Link to="/regions"
                                className="hidden text-sm font-semibold text-sky-600 hover:text-sky-500 sm:block">
                                {t('home.sections.popularAreas.viewAll')}
                                <span aria-hidden="true"> →</span>
                            </Link>
                        </div>

                        <div className="relative mt-8 -mx-4 sm:-mx-6 lg:mx-0">
                            <div className="overflow-x-auto px-4 sm:px-6 lg:px-0 lg:overflow-visible">
                                <ul
                                    role="list"
                                    className="inline-flex space-x-8 lg:grid lg:grid-cols-3 lg:gap-x-8 lg:space-x-0"
                                >
                                    {regions.map((region) => (
                                        <li key={`region-grid-${region.id}`}
                                            className="inline-flex w-64 flex-shrink-0 flex-col lg:w-auto mb-10">
                                            <Link to={`/region/${region.id}`} className="group relative">
                                                <div
                                                    className="aspect-h-1 aspect-w-1 w-full overflow-hidden rounded bg-gray-200">
                                                    <img
                                                        alt={region.description || region.name}
                                                        src={region.image1}
                                                        className="h-full w-full object-cover object-center group-hover:opacity-75"
                                                    />
                                                </div>
                                                <div className="relative mt-3">
                                                    <h3 className="text-lg font-bold text-gray-900 truncate">
                                                        {region.name}
                                                        <span className="ml-2" aria-hidden="true">→</span>
                                                    </h3>

                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="mt-12 px-4 sm:hidden">
                            <Link to="/regions" className="text-sm font-semibold text-sky-600 hover:text-sky-500">
                                {t('home.sections.popularAreas.viewAll')}
                                <span aria-hidden="true"> →</span>
                            </Link>
                        </div>
                    </div>
                </section>

                <div className=" py-24 sm:py-32 bg-sky-900">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="mx-auto max-w-xl text-center">
                            <h2 className="text-lg font-semibold leading-8 tracking-tight text-sky-600"></h2>
                            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                {t('home.sections.reviews.title')}
                            </p>
                        </div>
                        <div className="mx-auto mt-16 flow-root max-w-2xl sm:mt-20 lg:mx-0 lg:max-w-none">
                            <TestimonialSlider />
                        </div>
                    </div>
                </div>

            </div>
        </>
    );
}